import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select } from '../../../../components/Select'
import { UserSelect } from '../../../../components/Select/UserSelect'
import { SectorSelect } from '../../../../components/Select/SectorSelect'
import { Button } from '../../../../components/Button'
import { Input } from '../../../../components'
import { Textarea } from '../../../../components/Textarea'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/Forsm/form"
import { sistemaFormSchema, type SistemaFormValues } from './schema'
import type { User, CreateSistemaRequest, Sistema } from '@/models'
import { FaServer, FaPlus, FaTimes, FaUserShield, FaBox } from "react-icons/fa"
import { useSetores } from '../../../demands/hooks/useData'

interface SistemaFormProps {
  usuarios: User[]
  onSubmit: (data: CreateSistemaRequest) => Promise<void>
  isLoading?: boolean
  initialData?: Partial<Sistema>
  isEdit?: boolean
}

interface ModuloInput {
  id?: number
  nome: string
  descricao: string
}

interface PerfilInput {
  id?: number
  nome: string
  descricao: string
}

export const SistemsForm = ({ 
  usuarios, 
  onSubmit, 
  isLoading = false, 
  initialData,
  isEdit = false 
}: SistemaFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const { setores, loading: isLoadingSetores } = useSetores()

  // Estados para módulos e perfis dinâmicos
  const [modulos, setModulos] = useState<ModuloInput[]>(
    initialData?.modulos?.map(m => ({ id: m.id, nome: m.nome, descricao: m.descricao || '' })) || []
  )
  const [perfis, setPerfis] = useState<PerfilInput[]>(
    initialData?.perfis?.map(p => ({ id: p.id, nome: p.nome, descricao: p.descricao || '' })) || []
  )

  const form = useForm<SistemaFormValues>({
    resolver: zodResolver(sistemaFormSchema),
    defaultValues: {
      nome: initialData?.nome || "",
      descricao: initialData?.descricao || "",
      ...(initialData?.categoria ? { categoria: initialData.categoria } : {}),
      versao_atual: initialData?.versao_atual || "",
      proprietario_fk: initialData?.proprietario_fk?.toString() || "",
      gestor_tecnico_fk: initialData?.gestor_tecnico_fk?.toString() || "",
      status_ciclo_vida: initialData?.status_ciclo_vida || "",
      fornecedor: initialData?.fornecedor || "",
      tipo_licenca: initialData?.tipo_licenca || "",
      quantidade_licencas: initialData?.quantidade_licencas?.toString() || "",
      data_expiracao: initialData?.data_expiracao || "",
      data_renovacao: initialData?.data_renovacao || "",
      custo_anual: initialData?.custo_anual?.toString() || "",
      servidores_infraestrutura: initialData?.servidores_infraestrutura || "",
      servico_negocio_relacionado: initialData?.servico_negocio_relacionado || "",
      documentacao_link: initialData?.documentacao_link || "",
      suporte_dr: initialData?.suporte_dr || "",
      suporte_dn: initialData?.suporte_dn || "",
      suporte_fornecedor: initialData?.suporte_fornecedor || "",
    },
  })

  // Opções de usuários (gestores técnicos)
  const userOptions = usuarios.map(user => ({
    value: user.id.toString(),
    label: user.name,
    name: user.name,
    email: user.email,
    ...(user.avatar && { avatar: user.avatar }),
  }))

  // Opções de setores (proprietários)
  const setorOptions = useMemo(() => {
    if (!setores) return []
    return setores.map(setor => ({
      value: setor.id.toString(),
      label: setor.nome_setor,
      nome_setor: setor.nome_setor,
      ...(setor.cidade?.name && { descricao: setor.cidade.name }),
    }))
  }, [setores])

  // Opções de categoria
  const categoriaOptions = [
    { value: 'Software de Negócio', label: 'Software de Negócio' },
    { value: 'Banco de Dados', label: 'Banco de Dados' },
    { value: 'Sistema Operacional', label: 'Sistema Operacional' },
    { value: 'Web', label: 'Web' },
  ]

  // Funções para gerenciar módulos
  const addModulo = () => {
    setModulos([...modulos, { nome: '', descricao: '' }])
  }

  const removeModulo = (index: number) => {
    setModulos(modulos.filter((_, i) => i !== index))
  }

  const updateModulo = (index: number, field: 'nome' | 'descricao', value: string) => {
    const updated = [...modulos]
    updated[index][field] = value
    setModulos(updated)
  }

  // Funções para gerenciar perfis
  const addPerfil = () => {
    setPerfis([...perfis, { nome: '', descricao: '' }])
  }

  const removePerfil = (index: number) => {
    setPerfis(perfis.filter((_, i) => i !== index))
  }

  const updatePerfil = (index: number, field: 'nome' | 'descricao', value: string) => {
    const updated = [...perfis]
    updated[index][field] = value
    setPerfis(updated)
  }

  const onFormSubmit = async (data: SistemaFormValues) => {
    try {
      setIsSubmitting(true)
      
      const requestData: CreateSistemaRequest = {
        nome: data.nome,
        descricao: data.descricao,
        categoria: data.categoria,
        versao_atual: data.versao_atual,
        proprietario_fk: Number(data.proprietario_fk),
        gestor_tecnico_fk: Number(data.gestor_tecnico_fk),
        status_ciclo_vida: data.status_ciclo_vida,
        fornecedor: data.fornecedor,
        tipo_licenca: data.tipo_licenca,
        quantidade_licencas: Number(data.quantidade_licencas),
        custo_anual: Number(data.custo_anual),
        servidores_infraestrutura: data.servidores_infraestrutura,
        servico_negocio_relacionado: data.servico_negocio_relacionado,
        suporte_dr: data.suporte_dr,
        suporte_dn: data.suporte_dn,
        suporte_fornecedor: data.suporte_fornecedor,
        modulos: modulos.filter(m => m.nome.trim() !== ''),
        perfis: perfis.filter(p => p.nome.trim() !== ''),
        ...(data.data_expiracao ? { data_expiracao: data.data_expiracao } : {}),
        ...(data.data_renovacao ? { data_renovacao: data.data_renovacao } : {}),
        ...(data.documentacao_link ? { documentacao_link: data.documentacao_link } : {}),
      }

      await onSubmit(requestData)
      form.reset()
      navigate('/gestao/sistemas')
    } catch (error) {
      console.error('Erro ao salvar sistema:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700">
      <div className="px-6 py-8 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <FaServer className="text-blue-600 dark:text-blue-400" />
            {isEdit ? 'Editar Sistema' : 'Cadastrar Novo Sistema'}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {isEdit 
              ? 'Modifique as informações do sistema de TI.'
              : 'Registre um novo sistema no inventário de TI da organização.'
            }
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-8">
            
            {/* Seção: Informações Básicas */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Informações Básicas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="nome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Sistema *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: SAP ERP" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="categoria"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria *</FormLabel>
                      <FormControl>
                        <Select
                          options={categoriaOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Selecione a categoria"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="versao_atual"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Versão Atual *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: 2024.1.5" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status_ciclo_vida"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status do Ciclo de Vida *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Produção, Desenvolvimento" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="md:col-span-2">
                  <FormField
                    control={form.control}
                    name="descricao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descrição *</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Descreva o propósito e funcionalidades principais do sistema" 
                            {...field} 
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Seção: Responsabilidade */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Responsabilidade e Propriedade
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="proprietario_fk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Setor Proprietário *</FormLabel>
                      <FormControl>
                        <SectorSelect
                          options={setorOptions}
                          value={field.value}
                          onChange={(val) => field.onChange(String(val))}
                          placeholder={isLoadingSetores ? 'Carregando setores...' : 'Selecione o setor'}
                          disabled={isLoadingSetores}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gestor_tecnico_fk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gestor Técnico *</FormLabel>
                      <FormControl>
                        <UserSelect
                          variant='user'
                          options={userOptions}
                          value={field.value}
                          onChange={(val) => field.onChange(String(val))}
                          placeholder="Selecione o gestor técnico"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Seção: Licenciamento */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Licenciamento e Fornecedor
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="fornecedor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fornecedor *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Microsoft, Oracle" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tipo_licenca"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Licença *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Perpétua, Assinatura" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="quantidade_licencas"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Qtd. de Licenças *</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="custo_anual"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Custo Anual (R$) *</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="0.00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="data_expiracao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Expiração</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="data_renovacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Renovação</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Seção: Infraestrutura */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Infraestrutura e Serviços
              </h3>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="servidores_infraestrutura"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Servidores/Infraestrutura *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Ex: Servidor AWS EC2 - us-east-1, 4 vCPUs, 16GB RAM" 
                          {...field} 
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="servico_negocio_relacionado"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Serviço de Negócio Relacionado *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Ex: Gestão Financeira, Atendimento ao Cliente" 
                          {...field} 
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="documentacao_link"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Link da Documentação</FormLabel>
                      <FormControl>
                        <Input type="url" placeholder="https://..." {...field} />
                      </FormControl>
                      <FormDescription>
                        URL para documentação técnica ou manual do sistema
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Seção: Suporte */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Informações de Suporte
              </h3>
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="suporte_dr"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suporte DR (Disaster Recovery) *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Descreva o plano de recuperação de desastres" 
                          {...field} 
                          rows={2}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="suporte_dn"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suporte DN (Data Network) *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Informações sobre suporte de rede" 
                          {...field} 
                          rows={2}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="suporte_fornecedor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suporte do Fornecedor *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Contatos e informações de suporte do fornecedor" 
                          {...field} 
                          rows={2}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Seção: Módulos */}
            <div className="border-b border-gray-200 dark:border-neutral-700 pb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <FaBox className="text-blue-600 dark:text-blue-400" />
                  Módulos do Sistema
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addModulo}
                >
                  <FaPlus className="mr-2" /> Adicionar Módulo
                </Button>
              </div>
              
              {modulos.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                  Nenhum módulo adicionado. Clique em &quot;Adicionar Módulo&quot; para começar.
                </p>
              ) : (
                <div className="space-y-4">
                  {modulos.map((modulo, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-gray-50 dark:bg-neutral-900 rounded-lg">
                      <div className="flex-1 space-y-3">
                        <Input
                          placeholder="Nome do módulo"
                          value={modulo.nome}
                          onChange={(e) => updateModulo(index, 'nome', e.target.value)}
                        />
                        <Input
                          placeholder="Descrição do módulo"
                          value={modulo.descricao}
                          onChange={(e) => updateModulo(index, 'descricao', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeModulo(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Seção: Perfis */}
            <div className="pb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <FaUserShield className="text-blue-600 dark:text-blue-400" />
                  Perfis de Acesso
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPerfil}
                >
                  <FaPlus className="mr-2" /> Adicionar Perfil
                </Button>
              </div>
              
              {perfis.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                  Nenhum perfil adicionado. Clique em &quot;Adicionar Perfil&quot; para começar.
                </p>
              ) : (
                <div className="space-y-4">
                  {perfis.map((perfil, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-gray-50 dark:bg-neutral-900 rounded-lg">
                      <div className="flex-1 space-y-3">
                        <Input
                          placeholder="Nome do perfil"
                          value={perfil.nome}
                          onChange={(e) => updatePerfil(index, 'nome', e.target.value)}
                        />
                        <Input
                          placeholder="Descrição do perfil"
                          value={perfil.descricao}
                          onChange={(e) => updatePerfil(index, 'descricao', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePerfil(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-4 justify-end pt-6 border-t border-gray-200 dark:border-neutral-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/gestao/sistemas')}
                disabled={isSubmitting || isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isLoading}
                isLoading={isSubmitting}
              >
                {isEdit ? 'Atualizar Sistema' : 'Cadastrar Sistema'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
